import React, { useCallback, useEffect, useRef, useState } from 'react';
import AppLayout from './Layout';
import { useOrganization } from '../context/OrganizationContext';

function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

function getDeviceHealth(lastSeen, status) {
  if (String(status || '').toLowerCase() !== 'active') return 'offline';
  if (!lastSeen) return 'offline';

  const seen = new Date(lastSeen).getTime();
  if (Number.isNaN(seen)) return 'offline';

  const diffMinutes = (Date.now() - seen) / 60000;
  if (diffMinutes <= 2) return 'healthy';
  if (diffMinutes <= 5) return 'stale';
  return 'offline';
}

function badgeStyle(status) {
  const value = String(status || '').toLowerCase();
  if (value === 'healthy' || value === 'active') {
    return { background: '#dcfce7', color: '#166534' };
  }
  if (value === 'stale' || value === 'pending') {
    return { background: '#fef3c7', color: '#92400e' };
  }
  return { background: '#fee2e2', color: '#991b1b' };
}

export default function DevicesPage({ onLogout }) {
  const {
    activeOrganization,
    organizationLoading,
    organizationError
  } = useOrganization();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);

  const loadData = useCallback(async () => {
    const organizationGuid = activeOrganization?.guid;
    if (!organizationGuid) {
      setDevices([]);
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required.');
      setDevices([]);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');

    try {
      const baseUrl = process.env.REACT_APP_CYBEDEFENDER_AI_URL || '/api';
      const response = await fetch(
        `${baseUrl}/cybedefender/organizations/${encodeURIComponent(organizationGuid)}/devices?page=1&page_size=100`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Devices request failed: ${response.status}`);
      }

      const data = await response.json();
      if (requestId === requestIdRef.current) {
        setDevices(Array.isArray(data.devices) ? data.devices : []);
      }
    } catch (loadError) {
      if (requestId === requestIdRef.current) {
        setDevices([]);
        setError(loadError.message || 'Failed to load devices.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [activeOrganization?.guid]);

  useEffect(() => {
    requestIdRef.current += 1;
    setDevices([]);
    setError('');

    if (!activeOrganization?.guid) return undefined;

    loadData();
    const timer = setInterval(loadData, 15000);

    return () => {
      clearInterval(timer);
      requestIdRef.current += 1;
    };
  }, [activeOrganization?.guid, loadData]);

  let emptyMessage = 'No devices found for this organization.';
  if (organizationLoading) emptyMessage = 'Loading organization...';
  else if (!activeOrganization) emptyMessage = 'No organization is available for this account.';
  else if (loading) emptyMessage = 'Loading devices...';

  return (
    <AppLayout onLogout={onLogout}>
            <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Devices</h2>
            <p className="mt-1 text-sm text-slate-500">
              Tenant-scoped agent devices for the active organization.
            </p>
          </div>
          <button
            type="button"
            onClick={loadData}
            disabled={!activeOrganization || loading}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {(organizationError || error) && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {organizationError || error}
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="p-3 text-left font-medium">Device</th>
                <th className="p-3 text-left font-medium">Health</th>
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium">Agent Version</th>
                <th className="p-3 text-left font-medium">Last Seen</th>
                <th className="p-3 text-left font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 ? (
                <tr>
                  <td className="p-6 text-center text-slate-500" colSpan="6">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                devices.map((device) => {
                  const health = getDeviceHealth(device.last_seen_at, device.status);
                  return (
                    <tr key={device.guid} className="border-t border-slate-100">
                      <td className="p-3 font-medium text-slate-900">
                        {device.display_name || '-'}
                      </td>
                      <td className="p-3">
                        <span
                          className="rounded-full px-3 py-1 text-xs font-semibold capitalize"
                          style={badgeStyle(health)}
                        >
                          {health}
                        </span>
                      </td>
                      <td className="p-3">
                        <span
                          className="rounded-full px-3 py-1 text-xs font-semibold capitalize"
                          style={badgeStyle(device.status)}
                        >
                          {device.status || '-'}
                        </span>
                      </td>
                      <td className="p-3 text-slate-600">{device.agent_version || '-'}</td>
                      <td className="p-3 text-slate-600">{formatDate(device.last_seen_at)}</td>
                      <td className="p-3 text-slate-600">{formatDate(device.created_at)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
          </AppLayout>
  );
}
